import { useEffect, useMemo, useRef, useState } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { getHolidayName } from '@/lib/utils/holidays';
import { useLeaveCalendar, useMyLeave, useSaveLeavePlans, type LeavePeriod } from '@/pages/mypage/useLeave';

/**
 * 휴무 계획서 — <b>달력에서 날짜를 골라 저장한다</b> (사용자 지시 2026-08-26).
 *
 * <h2>기안을 거치지 않는다</h2>
 * 예전에는 계획서도 결재 서식({@code LEAVE_PLAN})이었다. 「언제 쉬겠다」 를 적자고 결재선을 짜야
 * 했고, 날짜는 <b>쉼표로 구분한 글자</b>로 받았다(`2026-09-14, 2026-09-15 …`). 그래서 아무도 적지
 * 않았고, 적힌 것도 오타가 섞였다. 계획은 승인 대상이 아니라 <b>시기를 알리는 값</b>이므로 여기서
 * 바로 저장한다 — 잔여를 깎는 실제 사용(연차 신청)은 그대로 결재를 받는다.
 *
 * <h2>한 해가 한 벌이다</h2>
 * 화면에 보이는 것이 곧 저장될 것이다. 더한 날·뺀 날을 따로 보내지 않고 <b>그 해 전체</b>를 보낸다
 * (`PUT /v1/mypage/leave/plans`). 그래서 다 지우고 저장하는 것도 뜻이 분명하다 — 계획 없음이다.
 *
 * <h2>고를 수 없는 날은 눌리지 않는다</h2>
 * 이미 쓴 날(계획할 것이 남아 있지 않다) · 지난 날(계획은 앞날에 대한 것이다). 눌러 놓고 저장할 때
 * 거절하면 <b>무엇이 빠졌는지 모른 채</b> 넘어간다.
 *
 * <p><b>주말도 공휴일·연휴도 고를 수 있다</b>(사용자 지시 2026-08-26). 잠갔던 때는 설·추석 연휴에
 * 교대로 근무하는 사람이 그 사흘을 계획에 적을 방법이 없었다 — 현장은 그날에도 돌아간다. 무심코
 * 찍는 것은 잠금이 아니라 <b>색</b>이 막는다(붉은 글자와 공휴일 이름이 그대로 뜬다).
 *
 * <h2>잔여를 넘겨 고를 수 없다</h2>
 * 앞으로의 계획 합계가 <b>잔여 연차</b>를 넘으면 그 이상은 눌리지 않는다(사용자 지시 2026-08-26).
 * 없는 연차를 계획해 두면 진척도의 분모가 처음부터 채울 수 없는 수가 되고, 12월에 「계획을 못 지킨
 * 사람」 으로 남는다. 잔여는 <b>지금 연차연도</b>의 것이고, 그 기간을 머리에 함께 적는다 — 기산이
 * 입사일이라 사람마다 다르다.
 *
 * <p>세는 것은 <b>앞으로의 계획</b>뿐이다. 이미 쉰 날은 잔여에서 벌써 빠졌으므로 함께 세면 두 번
 * 깎이고, 지난 날의 못 지킨 계획은 이제 와서 쓸 수 있는 날이 아니다.
 */
export function LeavePlanEditor({ year, onClose }: { year: number; onClose: () => void }) {
  const [target, setTarget] = useState(year);
  const calendar = useLeaveCalendar(target);
  const myLeave = useMyLeave();
  const save = useSaveLeavePlans();

  /** 고른 날들. `date → 일수(1 · 0.5)`. */
  const [picked, setPicked] = useState<Map<string, number>>(new Map());
  const [half, setHalf] = useState(false);
  const [loadedYear, setLoadedYear] = useState<number | null>(null);

  // 서버의 계획을 한 번만 옮겨 담는다 — 저장 뒤 응답이 다시 오는데, 그때 덮으면 사람이 고르던
  // 것이 사라진다. 해를 바꾸면 그 해 것을 다시 담는다.
  useEffect(() => {
    if (!calendar.data || loadedYear === target) return;
    setPicked(new Map(calendar.data.plans.map((plan) => [plan.date, plan.days])));
    setLoadedYear(target);
  }, [calendar.data, loadedYear, target]);

  const usedDates = useMemo(
    // 취소된 날(V544)은 쓴 날이 아니다.
    () => new Map((calendar.data?.usages ?? []).filter((usage) => !usage.cancelled).map((usage) => [usage.date, usage])),
    [calendar.data],
  );

  const today = todayIso();

  /** 부여가 없으면 {@code null} — 0 이 아니다. 그때는 막지 않고 「부여 없음」 이라고만 적는다. */
  const period = myLeave.data?.current ?? null;
  // 잔여는 회계 기준(연차관리 표와 같은 숫자 · DOC-113). 서버 재시작 전(fiscal 없음)에만 입사일 연차연도 값으로 물러선다 —
  // 회계 잔여가 비어 있는(입사일 모름) 사람을 입사일 숫자로 메우면 표와 갈린다(2026-10-26).
  const fiscal = myLeave.data?.fiscal;
  const remaining = fiscal ? fiscal.remainingDays : period?.remainingDays ?? null;
  const step = half ? 0.5 : 1;

  /** 앞으로의 계획 합계. 잔여와 견주는 값이라 지난 날은 빼고 센다(위 주석 참고). */
  const plannedAhead = useMemo(() => {
    let sum = 0;
    for (const [date, days] of picked) if (date >= today) sum += days;
    return round(sum);
  }, [picked, today]);

  /** 더 고를 수 있는 일수. 잔여를 모르면 제한하지 않는다({@code Infinity}). */
  const budget = remaining == null ? Number.POSITIVE_INFINITY : round(remaining - plannedAhead);

  /**
   * 잔여를 넘겨 누른 순간. 칸이 눌리지 않는 것만으로는 <b>왜</b> 안 되는지 알 수 없어 한 줄로 말한다.
   * 하나라도 빼면 사라진다 — 남은 자리가 다시 생겼다는 뜻이다.
   */
  const [hitCap, setHitCap] = useState(false);
  useEffect(() => {
    if (budget >= step) setHitCap(false);
  }, [budget, step]);

  /**
   * 드래그. 누른 <b>첫 칸이 방향을 정한다</b> — 이미 고른 날에서 시작하면 지우는 드래그, 빈 날에서
   * 시작하면 찍는 드래그다. 지나간 칸마다 켜고 끄면 손이 왕복할 때 값이 뒤집혀 아무것도 못 고른다.
   */
  const dragMode = useRef<'add' | 'remove' | null>(null);
  useEffect(() => {
    const stop = () => {
      dragMode.current = null;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  /**
   * 한 칸을 찍거나 지운다. <b>한도는 여기서 본다</b> — 드래그는 칸마다 이 함수를 부르므로, 바깥에서
   * 한 번만 재면 끌고 지나간 날이 한도를 넘어 들어간다. 최신 상태({@code prev})로 다시 세는 이유도
   * 같다: 드래그 중에는 렌더가 따라오기 전에 다음 칸이 들어온다.
   */
  const apply = (date: string, mode: 'add' | 'remove') => {
    setPicked((prev) => {
      if (mode === 'remove') {
        if (!prev.has(date)) return prev;
        const next = new Map(prev);
        next.delete(date);
        return next;
      }

      if (prev.get(date) === step) return prev;
      if (remaining != null && date >= today) {
        let ahead = 0;
        for (const [at, days] of prev) if (at >= today && at !== date) ahead += days;
        if (round(ahead + step) > remaining) {
          setHitCap(true);
          return prev;
        }
      }
      const next = new Map(prev);
      next.set(date, step);
      return next;
    });
  };

  const onDown = (date: string) => {
    const mode = picked.get(date) === step ? 'remove' : 'add';
    dragMode.current = mode;
    apply(date, mode);
  };

  const onEnter = (date: string) => {
    if (dragMode.current) apply(date, dragMode.current);
  };

  const dates = [...picked.entries()].sort((left, right) => left[0].localeCompare(right[0]));
  const total = dates.reduce((sum, [, days]) => sum + days, 0);
  const upcoming = dates.filter(([date]) => date >= today);

  // 저장하지 않고 닫으면 고른 것이 사라진다. 달력에서 스무 날을 찍은 사람에게는 큰 손실이라 묻는다
  // (`FolderShareModal` 과 같은 방식). 고친 것이 없으면 묻지 않는다.
  const saved = (calendar.data?.plans ?? []).map((plan) => `${plan.date}:${plan.days}`).sort().join(',');
  const now = dates.map(([date, days]) => `${date}:${days}`).join(',');
  const dirty = loadedYear === target && saved !== now;

  const onSave = () => {
    save.mutate(
      { year: target, dates: dates.map(([date, days]) => ({ date, days })) },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="휴무 계획서"
      onClose={onClose}
      closeDisabled={save.isPending}
      beforeClose={() => !dirty || window.confirm('저장하지 않은 계획이 있습니다. 닫을까요?')}
      maxWidth="900px"
      footer={
        <>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginRight: 'auto' }}>
            {dates.length === 0
              ? '고른 날이 없습니다 — 이대로 저장하면 이 해의 계획이 지워집니다.'
              : `${target}년 ${dates.length}일 · 합계 ${round(total)}일 (앞으로 ${upcoming.length}일)`}
          </span>
          <button className="btn-secondary" onClick={onClose} disabled={save.isPending}>취소</button>
          <button className="btn-primary" onClick={onSave} disabled={save.isPending || calendar.isPending}>
            {save.isPending ? '저장 중…' : '저장'}
          </button>
        </>
      }
    >
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn-secondary" onClick={() => setTarget(target - 1)} disabled={save.isPending}>이전 해</button>
        <b style={{ fontSize: '15px' }}>{target}년</b>
        <button className="btn-secondary" onClick={() => setTarget(target + 1)} disabled={save.isPending}>다음 해</button>

        {/* 찍는 값. 반차 계획이 흔하지는 않지만, 없으면 반나절 계획을 하루로 적게 된다. */}
        <div className="flex" style={{ marginLeft: '6px' }}>
          {([false, true] as const).map((value) => (
            <button
              key={String(value)}
              onClick={() => setHalf(value)}
              className={half === value ? 'btn-primary' : 'btn-secondary'}
              style={{
                fontSize: '12px',
                padding: '5px 10px',
                borderRadius: value ? '0 6px 6px 0' : '6px 0 0 6px',
                whiteSpace: 'nowrap',
              }}
            >
              {value ? '반차(0.5)' : '종일'}
            </button>
          ))}
        </div>

        <span className="ml-auto flex items-center gap-3" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          <Swatch background="var(--accent)" label="계획" />
          <Swatch background="#cbd5e1" label="이미 사용" />
          <span>드래그로 여러 날을 한 번에</span>
        </span>
      </div>

      {/*
        잔여를 <b>맨 위에</b> 둔다. 며칠을 더 고를 수 있는지 모르는 채 달력부터 찍으면, 다 찍고 나서
        「이만큼은 안 된다」 를 듣게 된다(사용자 지시 2026-08-26).
      */}
      <Budget
        remaining={remaining}
        plannedAhead={plannedAhead}
        budget={budget}
        period={period}
        loading={myLeave.isPending}
      />

      {hitCap && (
        <Notice tone="warning">
          잔여 연차 <b>{remaining}일</b>을 모두 계획했습니다 — 더 고르려면 고른 날을 먼저 빼 주세요.
        </Notice>
      )}

      <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
        날짜를 누르거나 끌어서 고릅니다. 고른 날을 다시 누르면 빠집니다.{' '}
        <b>주말과 공휴일·연휴도 고를 수 있습니다</b>(현장 근무일 · 붉은 글자로 표시됩니다).
        이미 쓴 날과 지난 날은 고를 수 없습니다.
      </p>

      {calendar.isPending ? (
        <div className="flex justify-center py-10"><div className="spinner w-8 h-8" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" style={{ userSelect: 'none' }}>
          {MONTHS.map((month) => (
            <MonthGrid
              key={month}
              year={target}
              month={month}
              picked={picked}
              usedDates={usedDates}
              today={today}
              // 남은 자리보다 큰 값은 애초에 눌리지 않는다 — 반차로 바꾸면 0.5 만큼은 다시 열린다.
              full={budget < step}
              onDown={onDown}
              onEnter={onEnter}
            />
          ))}
        </div>
      )}

      {save.error && (
        <Notice tone="error">
          {save.error instanceof Error ? save.error.message : '휴무 계획을 저장하지 못했습니다.'}
        </Notice>
      )}

      {/* 결재와의 경계를 여기서 한 번 말한다 — 저장했는데 왜 잔여가 그대로냐는 물음이 여기서 난다. */}
      <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
        계획은 결재 없이 바로 저장되고, 팀장·인사총무팀의 「휴무 관리」 에 함께 보입니다.
        <b> 잔여 연차는 줄지 않습니다</b> — 실제로 쉬는 날은 「연차 신청」 으로 결재를 올려 주세요.
      </p>
    </Modal>
  );
}

// ── 잔여 ─────────────────────────────────────────────────────────────────────

/**
 * 잔여 · 계획 · 남은 자리 세 숫자와 막대 하나.
 *
 * <p>막대는 <b>잔여를 100 으로</b> 놓고 앞으로의 계획이 얼마나 찼는지를 보인다 — 이 화면에서 묻는
 * 것이 「며칠을 더 고를 수 있나」 여서다. 부여가 없으면 막대를 그리지 않는다: 분모가 없다.
 */
function Budget({
  remaining,
  plannedAhead,
  budget,
  period,
  loading,
}: {
  remaining: number | null;
  plannedAhead: number;
  budget: number;
  period: LeavePeriod | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div style={budgetBox}>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>잔여 연차를 불러오는 중…</span>
      </div>
    );
  }

  if (remaining == null) {
    return (
      <Notice tone="info">
        등록된 연차 부여가 없어 <b>잔여를 확인할 수 없습니다</b> — 계획은 세울 수 있지만 일수는 막지
        않습니다. 입사일이 인사 정보에 등록되면 다음 날부터 자동으로 부여됩니다.
      </Notice>
    );
  }

  const filled = remaining > 0 ? Math.min(1, plannedAhead / remaining) : 1;

  return (
    <div style={budgetBox}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          잔여 연차 <b style={{ fontSize: '17px', color: 'var(--accent)' }}>{round(remaining)}일</b>
        </span>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          앞으로의 계획 <b style={{ color: 'var(--text-primary)' }}>{round(plannedAhead)}일</b>
        </span>
        <span style={{ fontSize: '13px', color: budget <= 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
          더 고를 수 있는 날 <b>{round(Math.max(0, budget))}일</b>
        </span>
        {period && (
          // 기산이 입사일이라 사람마다 기간이 다르다. 「올해」 라고만 적으면 12월에 어긋난다.
          <span className="ml-auto" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            지금 연차연도 {period.startDate} ~ {period.endDate} · {period.expiresOn} 소멸
          </span>
        )}
      </div>

      <span
        style={{
          display: 'block',
          marginTop: '6px',
          height: '6px',
          borderRadius: '3px',
          background: 'var(--bg-hover)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${filled * 100}%`,
            background: budget <= 0 ? 'var(--danger)' : 'var(--accent)',
          }}
        />
      </span>
    </div>
  );
}

const budgetBox = {
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '10px 12px',
} as const;

// ── 한 달 ────────────────────────────────────────────────────────────────────

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function MonthGrid({
  year,
  month,
  picked,
  usedDates,
  today,
  full,
  onDown,
  onEnter,
}: {
  year: number;
  month: number;
  picked: Map<string, number>;
  usedDates: Map<string, { leaveType: string | null }>;
  today: string;
  /** 잔여를 다 계획했다 — 이미 고른 날은 뺄 수 있어야 하므로 <b>빈 칸만</b> 잠근다. */
  full: boolean;
  onDown: (date: string) => void;
  onEnter: (date: string) => void;
}) {
  const first = new Date(year, month - 1, 1);
  const length = new Date(year, month, 0).getDate();
  // 1일이 무슨 요일인지에 맞춰 앞을 비운다 — 요일이 맞지 않으면 주말 색이 거짓말을 한다.
  const blanks = first.getDay();

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '6px' }}>
      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
        {month}월
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {WEEKDAYS.map((label, index) => (
          <span
            key={label}
            style={{
              fontSize: '9px',
              textAlign: 'center',
              color: index === 0 ? '#dc2626' : index === 6 ? '#2563eb' : 'var(--text-muted)',
            }}
          >
            {label}
          </span>
        ))}
        {Array.from({ length: blanks }, (_, index) => <span key={`blank-${index}`} />)}
        {Array.from({ length }, (_, index) => {
          const day = index + 1;
          const date = `${year}-${pad(month)}-${pad(day)}`;
          const cell = new Date(year, month - 1, day);
          const holiday = getHolidayName(cell);
          const weekend = cell.getDay() === 0 || cell.getDay() === 6;
          const used = usedDates.get(date);
          const past = date < today;
          const days = picked.get(date);
          const capped = full && !days;
          // 주말도 <b>공휴일·연휴도</b> 고를 수 있다 — 현장은 그날에도 돌아간다(사용자 지시 2026-08-26).
          // 잠갔더니 설·추석 연휴에 교대로 쉬는 사람이 그 사흘을 계획에 적을 방법이 없었다. 법정
          // 유급휴일에 연차를 쓸 일이 없다는 것은 <b>평균</b>이고, 이 화면은 개인의 계획을 받는다.
          // 남는 위험(무심코 찍기)은 색과 말풍선이 맡는다 — 붉은 글자와 「공휴일」 이 그대로 뜬다.
          const disabled = Boolean(used || past || capped);

          return (
            <button
              key={date}
              type="button"
              disabled={disabled}
              onMouseDown={() => onDown(date)}
              onMouseEnter={() => onEnter(date)}
              // 공휴일은 이제 고를 수 있으므로 <b>덧붙인다</b> — 「추석 연휴」 와 「계획 1일」 은 서로를
              // 밀어내는 말이 아니다. 하나만 띄우던 때는 연휴에 찍은 계획이 말풍선에서 사라졌다.
              title={[
                date,
                holiday,
                used ? `이미 사용(${used.leaveType ?? '휴가'})` : null,
                past && !used ? '지난 날' : null,
                days ? `계획 ${days}일` : null,
                capped ? '잔여 연차를 모두 계획했습니다' : null,
              ].filter(Boolean).join(' · ')}
              style={{
                height: '22px',
                borderRadius: '4px',
                border: days === 0.5 ? '1px dashed var(--accent)' : '1px solid transparent',
                fontSize: '10px',
                fontWeight: days ? 700 : 400,
                cursor: disabled ? 'default' : 'pointer',
                background: days === 1 ? 'var(--accent)' : used ? '#cbd5e1' : disabled ? 'var(--bg-hover)' : 'white',
                color:
                  days === 1
                    ? '#ffffff'
                    : days === 0.5
                      ? 'var(--accent)'
                      : disabled
                        ? 'var(--text-muted)'
                        // 고를 수 있어도 <b>주말임은 보인다</b> — 무심코 토·일을 찍는 일을 막는 것은
                        // 잠그는 것이 아니라 색이다(현장은 그날이 근무일이다).
                        : holiday || cell.getDay() === 0
                          ? '#dc2626'
                          : weekend
                            ? '#2563eb'
                            : 'var(--text-primary)',
                opacity: disabled && !used ? 0.55 : 1,
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Swatch({ background, label }: { background: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span style={{ width: '10px', height: '10px', borderRadius: '2px', background }} />
      {label}
    </span>
  );
}

/** 오늘(로컬). `toISOString()` 은 UTC 로 밀려 자정 무렵에 하루가 어긋난다. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
